import React, { useState } from 'react';
import { Location } from '../../../types';
import { X, Edit, Trash2, Map } from 'lucide-react';

interface BuildingManagerProps {
  locations: Location[];
  onAddLocation: (location: Omit<Location, 'id' | 'position'>) => Promise<string>;
  onUpdateLocation: (location: Location) => Promise<void>;
  onDeleteLocation: (locationId: string) => Promise<void>;
  onClose: () => void;
  onMoveOnMap: (locationId: string) => void;
}

export const BuildingManager: React.FC<BuildingManagerProps> = ({
  locations,
  onAddLocation,
  onUpdateLocation,
  onDeleteLocation,
  onClose,
  onMoveOnMap
}) => {
  const [newBuilding, setNewBuilding] = useState<{
    name: string;
  }>({
    name: '',
  });
  
  const [editingBuilding, setEditingBuilding] = useState<Location | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingBuildingId, setDeletingBuildingId] = useState<string | null>(null);

  // Get all buildings
  const buildings = locations.filter(loc => loc.type === 'building');
  const handleAddBuilding = async () => {
    if (newBuilding.name.trim()) {
      setIsAdding(true);
      try {
        await onAddLocation({
          name: newBuilding.name.trim(),
          type: 'building'
        });
        setNewBuilding({
          name: '',
        });
      } catch (error) {
        console.error('Failed to add building:', error);
      } finally {
        setIsAdding(false);
      }
    }
  };

  const handleStartEdit = (building: Location) => {
    setEditingBuilding(building);
  };
  const handleUpdateBuilding = async () => {
    if (editingBuilding && editingBuilding.name.trim()) {
      setIsUpdating(true);
      try {
        await onUpdateLocation(editingBuilding);
        setEditingBuilding(null);
      } catch (error) {
        console.error('Failed to update building:', error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleDeleteBuilding = async (buildingId: string) => {
    if (window.confirm('Are you sure you want to delete this building? This will also delete all classrooms in this building. This action cannot be undone.')) {
      setDeletingBuildingId(buildingId);
      try {
        await onDeleteLocation(buildingId);
      } catch (error) {
        console.error('Failed to delete building:', error);
      } finally {
        setDeletingBuildingId(null);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingBuilding(null);
  };

  return (
    <div className="bg-white rounded-lg p-6 w-full max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-stone-800">Manage Buildings</h2>
        <button
          onClick={onClose}
          className="text-stone-500 hover:text-stone-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Add new building form */}
      <div className="mb-6 bg-stone-50 p-4 rounded-md">
        <h3 className="text-md font-medium text-stone-700 mb-3">Add New Building</h3>
        <div className="flex gap-3 flex-wrap">
          <div className="flex-grow min-w-[200px]">
            <input
              type="text"
              value={newBuilding.name}
              onChange={(e) => setNewBuilding({ name: e.target.value })}
              placeholder="Building name"
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>          <button
            onClick={handleAddBuilding}
            disabled={!newBuilding.name.trim() || isAdding}
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isAdding ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Adding...
              </>
            ) : (
              'Add Building'
            )}
          </button>
        </div>
      </div>
      
      {/* Buildings list */}
      <div className="max-h-[60vh] overflow-y-auto">
        {buildings.length === 0 ? (
          <p className="text-center text-stone-500 py-8">
            No buildings created yet. Add your first building above.
          </p>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-stone-700 uppercase bg-stone-100">
              <tr>
                <th className="px-4 py-2 rounded-tl-lg">Name</th>
                <th className="px-4 py-2">Classrooms</th>
                <th className="px-4 py-2 rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {buildings.map(building => {
                const buildingClassrooms = locations.filter(
                  loc => loc.type === 'classroom' && loc.buildingId === building.id
                );
                
                return (
                  <tr key={building.id} className="hover:bg-stone-50">
                    {editingBuilding?.id === building.id ? (
                      <td className="px-4 py-3 border-b border-stone-200">
                        <input
                          type="text"
                          value={editingBuilding.name}
                          onChange={(e) => setEditingBuilding({
                            ...editingBuilding,
                            name: e.target.value
                          })}
                          className="w-full px-3 py-1 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </td>
                    ) : (
                      <td className="px-4 py-3 border-b border-stone-200">{building.name}</td>
                    )}
                    
                    <td className="px-4 py-3 border-b border-stone-200">
                      {buildingClassrooms.length} classroom{buildingClassrooms.length !== 1 ? 's' : ''}
                    </td>
                    
                    <td className="px-4 py-3 border-b border-stone-200">
                      {editingBuilding?.id === building.id ? (                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateBuilding}
                            disabled={isUpdating}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded disabled:bg-green-400 flex items-center gap-1"
                          >
                            {isUpdating ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                Saving...
                              </>
                            ) : (
                              'Save'
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isUpdating}
                            className="px-2 py-1 bg-stone-400 text-white text-xs rounded disabled:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleStartEdit(building)}
                            className="p-1 text-amber-600 hover:text-amber-800"
                            title="Edit Building"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onMoveOnMap(building.id)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Move on Map"
                          >
                            <Map className="h-4 w-4" />
                          </button>                          <button
                            onClick={() => handleDeleteBuilding(building.id)}
                            disabled={deletingBuildingId === building.id}
                            className="p-1 text-red-600 hover:text-red-800 disabled:text-red-400"
                            title="Delete Building"
                          >
                            {deletingBuildingId === building.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};